import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { articles, hotList } from "@/mocks/articles";

function ReadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min(100, (window.scrollY / h) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-transparent">
      <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />
    </div>
  );
}

function formatCount(n: number) {
  return n >= 10000 ? `${(n / 10000).toFixed(1)} 万` : `${n}`;
}

export default function ArticleDetail() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id);
  const related = articles.filter((a) => a.id !== id).slice(0, 3);

  usePageMeta({
    title: article ? `${article.title}｜黑料今日·${article.categoryName}` : "条目未找到｜黑料今日",
    description: article
      ? article.summary.slice(0, 110)
      : "该爆料条目不存在或已被移除，可返回每日日报列表继续浏览其他内容。",
    keywords: article ? `${article.tags.join(",")},黑料今日,每日爆料` : "黑料今日,每日日报",
    path: `/article/${id ?? ""}`,
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.summary,
            datePublished: article.date,
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: "黑料今日" },
            publisher: { "@type": "Organization", name: "黑料今日" },
            keywords: article.tags.join(","),
            articleSection: article.categoryName,
            mainEntityOfPage: `https://043r0.cn/article/${article.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://043r0.cn/" },
              { "@type": "ListItem", position: 2, name: "每日日报", item: "https://043r0.cn/daily" },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `https://043r0.cn/article/${article.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-24">
          <div className="text-center max-w-md">
            <span className="inline-flex w-16 h-16 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <i className="ri-file-unknow-line text-3xl" />
            </span>
            <h1 className="mt-5 font-heading font-black text-2xl text-foreground-950">条目未找到</h1>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              这条爆料可能已经下线或被移动到其他栏目，去日报列表看看今天更新了什么吧。
            </p>
            <Link
              to="/daily"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              返回每日日报
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <ReadingProgress />
      <SiteHeader />
      <main className="flex-1">
        <article>
          <header className="px-4 md:px-8 pt-8 md:pt-10 pb-6 border-b border-background-200 bg-background-100">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "每日日报", to: "/daily" },
                { label: article.categoryName },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
              <Link
                to={`/daily?cat=${article.category}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 font-medium cursor-pointer"
              >
                <i className="ri-price-tag-3-line" />
                {article.categoryName}
              </Link>
              {article.tags.map((t) => (
                <span key={t} className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-900">
                  #{t}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-heading font-black text-2xl md:text-4xl text-foreground-950 leading-tight max-w-4xl">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {article.date} {article.time}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatCount(article.readCount)} 阅读
              </span>
              <span className="flex items-center gap-1.5 text-primary-600">
                <i className="ri-fire-line" />
                热度 {article.hot}
              </span>
            </div>
          </header>

          <div className="px-4 md:px-8 py-8 md:py-12">
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-8 min-w-0">
                <figure className="rounded-lg overflow-hidden bg-background-200 h-56 md:h-80">
                  <img
                    src={article.cover}
                    alt={`${article.title} - ${article.categoryName}`}
                    title={`${article.title} ${article.categoryName}`}
                    className="w-full h-full object-cover object-top"
                  />
                </figure>

                <p className="mt-6 text-base text-foreground-700 leading-loose border-l-2 border-primary-400 pl-4">
                  {article.summary}
                </p>

                <div className="mt-8 space-y-8">
                  {article.sections.map((sec, i) => (
                    <section key={sec.heading} id={`sec-${i}`} className="scroll-mt-28">
                      <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                        {sec.heading}
                      </h2>
                      <div className="mt-4 space-y-4">
                        {sec.paragraphs.map((p, pi) => (
                          <p
                            key={`${sec.heading}-${pi}`}
                            className="text-sm md:text-base text-foreground-700 leading-loose"
                          >
                            {p}
                          </p>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>

                <div className="mt-10 p-5 rounded-lg bg-background-100 border border-background-200">
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    <i className="ri-information-line mr-1" />
                    本文为娱乐资讯整理，内容基于公开讨论与网络信息，仅作信息参考，不构成任何事实认定。
                    如涉及权利问题，可通过页脚邮箱与我们联系。
                  </p>
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-28 space-y-5">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading font-bold text-base text-foreground-950">本文目录</h2>
                    <ol className="mt-4 space-y-1.5">
                      {article.sections.map((sec, i) => (
                        <li key={sec.heading}>
                          <a
                            href={`#sec-${i}`}
                            className="flex items-start gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                          >
                            <span className="text-primary-500 font-medium">{i + 1}.</span>
                            {sec.heading.replace(/^[一二三四五六七八九十]+、/, "")}
                          </a>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading font-bold text-base text-foreground-950">今日热榜</h2>
                    <ol className="mt-4 space-y-2">
                      {hotList.slice(0, 4).map((h, i) => (
                        <li key={h.id}>
                          <Link
                            to={`/article/${h.id}`}
                            className="flex items-start gap-2.5 text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
                          >
                            <span
                              className={`shrink-0 w-5 h-5 rounded flex items-center justify-center text-[11px] font-bold ${
                                i < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                              }`}
                            >
                              {i + 1}
                            </span>
                            <span className="line-clamp-2 leading-snug">{h.title}</span>
                          </Link>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </aside>
            </div>
          </div>

          <section className="px-4 md:px-8 pb-14 md:pb-20">
            <div className="flex items-center gap-3">
              <span className="w-1.5 h-7 rounded-full bg-primary-500" />
              <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950">
                相关推荐
              </h2>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        </article>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}